<template>
  <div class="w-full h-full px-5 mt-3">
    <transition-group class="flex flex-wrap items-stretch gap-3 rounded-lg" tag="div" mode="in-out" name="list">
      <div v-for="global_variable in global_variables.concat({ index: '__action' })" :key="global_variable.index">
        <div
          v-if="global_variable.index != '__action'"
          class="flex items-center justify-between w-full p-5 pt-1 pb-1 space-x-6 bg-white rounded-lg shadow-md dark:bg-dark-box-background">
          <TabGroup
            :selected-index="selected_tab_index"
            class="relative w-full transition ease-in-out rounded-lg focus:outline-none bg-light-box-background dark:bg-dark-box-background duration-side-menu-transition-duration"
            as="div"
            @change="selected_tab_index = $event">
            <TabList
              class="flex items-start relative border-b-[1px] dark:border-dark-divider-border rounded-tl-md shadow-sm rounded-tr-md focus:outline-none">
              <Tab
                v-for="tab in global_variable?.id ? ['Definition', 'Set Values'] : ['Definition']"
                :key="tab"
                v-slot="{ selected }"
                as="template">
                <div
                  :class="[selected ? '!text-blue font-normal' : 'font-normal']"
                  class="py-2 mx-[12px] transform translate-y-[3px] text-[#46545C] dark:text-common-grey hover:border-common-grey border-b-[2px] transition ease-in-out duration-300 rounded-sm relative focus:outline-none inline-flex ring-0 ring-offset-0 border-transparent cursor-pointer">
                  {{ tab }}
                  <div
                    :class="[
                      !selected ? 'scale-0' : '',
                      'absolute pointer-events-none transition transform ease-in-out duration-side-menu-transition-duration w-[100%]  h-[2px] rounded-full  bg-light-brand-blue dark:bg-dark-brand-blue bottom-[-1px]'
                    ]" />
                </div>
              </Tab>
            </TabList>

            <TabPanels class="flex flex-col max-w-[600px] max-h-[600px] min-w-[600px] min-h-[600px] overflow-hidden">
              <TabPanel v-if="selected_tab_index == 0" key="1" v-slot="{ selected }" static>
                <Form
                  v-slot="{ values }"
                  :key="global_variable.index"
                  :validation-schema="schema"
                  :initial-values="global_variable"
                  class="flex flex-col"
                  @submit="save">
                  <keep-alive>
                    <div v-if="selected && selected_tab_index == 0">
                      <div class="flex flex-col items-start flex-1 gap-2 px-1 mt-2 space-y-1 truncate">
                        <TextInput name="name"
                                   type="text"
                                   :label="$t('Name')"
                                   classes="mt-2"
                                   :placeholder="$t('Name')" />
                        <TextArea
                          name="description"
                          type="text"
                          :label="$t('Description')"
                          classes="mt-2"
                          :placeholder="$t('Description')" />

                        <div class="relative flex items-center w-full">
                          <div class="flex-1 w-full min-w-0">
                            <div class="flex flex-row items-center w-full gap-1">
                              <SelectInput
                                name="input_type"
                                :items="input_types"
                                label-attr="label"
                                value-attr="value"
                                :allow-null="false"
                                :is-box-background="true"
                                :show-label="true"
                                :label="$t('Field type')"
                                classes="w-1/2" />

                              <SelectInput
                                v-if="values.input_type == 'Currency'"
                                :items="$consts.currencies.data"
                                label-attr="label"
                                value-attr="value"
                                :allow-null="false"
                                name="currency"
                                :placeholder="$t('Currency type')"
                                :is-box-background="true"
                                :show-label="true"
                                :label="$t('Currency')"
                                classes="w-full" />
                              <SelectInput
                                v-if="values.input_type == 'Decimal'"
                                :items="[
                                  { label: '1', value: '1' },
                                  { label: '2', value: '2' },
                                  { label: '3', value: '3' },
                                  { label: '4', value: '4' }
                                ]"
                                label-attr="label"
                                value-attr="value"
                                :allow-null="false"
                                name="decimal_places"
                                :placeholder="$t('Decimal Places')"
                                :is-box-background="true"
                                :show-label="true"
                                :label="$t('Decimal Places')"
                                classes="w-1/3" />
                            </div>
                            <div class="flex flex-col w-full gap-1">
                              <div
                                v-if="values.input_type == 'List' || values.input_type == 'Select' || values.input_type == 'Checklist'"
                                class="flex items-center justify-start w-full gap-2 my-2">
                                <TextInput
                                  name="option"
                                  :value="option"
                                  label=""
                                  :classes="'w-full'"
                                  type="text"
                                  :placeholder="$t('Type the option than click Add')" />

                                <button class="w-1/4 primary-btn" @click.prevent="addOption">Add</button>
                              </div>
                              <div v-if="options.length > 0" class="text-light-body-text dark:text-dark-body-text">
                                <ul>
                                  <li v-for="(item, index) in options" :key="index" class="ml-2 sm:text-sm">
                                    {{ item }}
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="flex flex-col justify-between w-full pt-2">
                          <TextInput name="default_value" type="text" label="Default Value" placeholder="Value" />

                          <div class="mt-2 py-0.5 text-xs font-medium justify-end overflow-visible w-full">
                            <Field v-slot="{ handleChange }" name="is_per_user">
                              <SwitchGroup as="div" class="flex items-end justify-between">
                                <span class="flex flex-col flex-grow">
                                  <SwitchLabel as="span" class="text-sm font-medium text-gray-900 dark:text-white" passive>{{
                                    $t("Is Per User")
                                  }}</SwitchLabel>
                                  <SwitchDescription as="span" class="text-sm text-common-grey">
                                    {{ $t("Can each team member set his/her own value?") }}.</SwitchDescription>
                                </span>
                                <div class="flex items-center pl-2 space-x-1 dark:text-white">
                                  <span :class="[!global_variable.is_per_user ? 'opacity-100' : 'opacity-0']">{{ $t("No") }}</span>
                                  <Switch
                                    :value="global_variable.is_per_user ? 't' : 'f'"
                                    :class="[
                                      global_variable.is_per_user ? 'bg-blue' : 'bg-gray-200',
                                      'disabled:opacity-70 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                                    ]"
                                    @update:model-value="
                                      () => {
                                        global_variable.is_per_user = !(global_variable.is_per_user == 't' ? true : false) ? 't' : 'f'
                                        handleChange(global_variable.is_per_user)
                                      }
                                    ">
                                    <span
                                      aria-hidden="true"
                                      :class="[
                                        global_variable.is_per_user && $store.state.portfolio.ui_options?.language == 'ar'
                                          ? '-translate-x-5'
                                          : global_variable.is_per_user
                                            ? 'translate-x-5'
                                            : 'translate-x-0',
                                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                      ]" />
                                  </Switch>
                                  <span :class="[global_variable.is_per_user ? 'opacity-100' : 'opacity-0']">{{ $t("Yes") }}</span>
                                </div>
                              </SwitchGroup>
                            </Field>
                          </div>
                        </div>
                      </div>
                    </div>
                  </keep-alive>
                  <div class="flex justify-between w-full mt-8 overflow-visible divide-gray-200">
                    <button type="button" class="w-1/4 m-2 mt-3 mr-0 cancel-btn" @click="removeGlobalVariable(global_variable)">
                      {{ $t("Delete") }}
                    </button>

                    <button
                      :disabled="
                        !isGlobalVariableChanged({ ...global_variable, ...values }) ||
                          !isGlobalVariableValid({ ...global_variable, ...values })
                      "
                      class="w-1/4 m-2 mt-3 mr-0 primary-btn"
                      type="submit">
                      {{ $t("Save") }}
                    </button>
                  </div>
                </Form>
              </TabPanel>
              <TabPanel v-if="selected_tab_index == 1"
                        key="2"
                        as="template"
                        static
                        class="relative">
                <keep-alive>
                  <div class="flex-1 mt-2">
                    <div class="absolute bottom-0 left-0 z-10 flex justify-between w-full mt-auto overflow-visible divide-gray-200">
                      <button type="button" class="w-1/4 m-2 mt-3 mr-0 cancel-btn" @click="removeGlobalVariable(global_variable)">
                        {{ $t("Delete") }}
                      </button>
                      <button class="w-1/4 m-2 mt-3 mr-0 primary-btn" type="button" @click="save(global_variable)">{{ $t("Save") }}</button>
                    </div>
                    <div class="pt-2 max-w-[600px] max-h-[400px] min-w-[600px] min-h-[400px] overflow-auto">
                      <Form
                        as="ul"
                        role="list"
                        :initial-data="global_variable.initial_values"
                        :schema="global_variable.schema"
                        class="flex-1 w-full h-full overflow-auto divide-y divide-gray-200 select-none dark:divide-gray-500">
                        <li
                          v-for="field in global_variable.values.filter((item) => $utils.getUserById(item.user, 'email'))"
                          :key="field.user"
                          class="relative mb-2 cursor-pointer">
                          <div class="relative flex flex-col items-start px-5 pt-6 group">
                            <div class="flex-1 block p-1 -m-1">
                              <div class="absolute inset-0 group-hover:bg-gray-100 dark:group-hover:bg-dark-divider" aria-hidden="true" />
                              <div class="relative flex items-center flex-1 min-w-0">
                                <span class="relative flex-shrink-0 inline-block">
                                  <Avatar :user="field.user" />
                                </span>
                                <div class="ml-4 truncate">
                                  <p class="text-sm font-medium text-gray-900 truncate dark:text-white">
                                    {{ $utils.getUserById(field.user, "full_name") }}
                                  </p>
                                  <p class="text-sm text-gray-500 truncate">
                                    {{ $utils.getUserById(field.user, "email") }}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div as="div" class="relative flex-shrink-0 inline-block w-full mt-1 mb-2 ml-2 text-left">
                              <MetricFieldInput
                                :metric-value="field.value"
                                :name="`${global_variable.reference_name}_${field.user}`"
                                :show-label="false"
                                :validate-on-input="false"
                                :metric="{
                                  name: `${global_variable.reference_name}_${field.user}`,
                                  reference_name: `${global_variable.reference_name}_${field.user}`,
                                  value: field.value,
                                  input_type: global_variable.input_type,
                                  data_type: global_variable.data_type,
                                  input_options: { ...global_variable.input_options }
                                }"
                                :record="null"
                                class="w-full"
                                @update:metric-value="(value) => updateFieldValue(global_variable, field.user, value)" />
                            </div>
                          </div>
                        </li>
                      </Form>
                    </div>
                  </div>
                </keep-alive>
              </TabPanel>
            </TabPanels>
          </TabGroup>
        </div>
        <div
          v-else
          class="flex items-center justify-center w-full h-full p-20 m-0 bg-white rounded-lg justify-self-stretch dark:bg-dark-box-background hover:bg-gray-200 dark:hover:opacity-80 hover:cursor-pointer"
          @click="addVariable">
          <span class="flex items-center justify-center font-semibold dark:text-white">
            <Plus class="w-10 h-10" />
            {{ $t("Add Variable") }}
          </span>
        </div>
      </div>
    </transition-group>
    <Modal :is-open="show_object_delete_confirm_modal" max-width="max-w-[33%] mx-5" default-success-msg="Success">
      <template #form>
        <h3 class="text-2xl font-medium leading-10 text-center text-gray-900 capitalize dark:text-white">
          <span>{{ $t("Delete Global Var ?") }} </span>
        </h3>

        <div class="px-4 pb-4 mt-2 mb-2 bg-white rounded-lg dark:bg-dark-screen-background dark:text-white sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <div class="mt-2">
                <p class="text-gray-500">
                  {{ $t("Do you want to delete") }} <span class="font-semibold capitalize">{{ to_be_deleted_object.name }}</span>
                  {{ $t("from Global variables") }}
                  ?
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-white bg-red-400 border border-gray-300 rounded-md shadow-sm hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            @click="confirmObjectDelete">
            <span>{{ $t("Yes") }}</span>
          </button>

          <button
            type="button"
            class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm dark:bg-dark-box-background dark:text-white dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            @click="show_object_delete_confirm_modal = false">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import * as Yup from "yup"
import { Form, Field } from "vee-validate"
import Modal from "@/components/shared/Modal.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import TextInput from "@/components/shared/TextInput.vue"
import TextArea from "@/components/shared/TextArea.vue"
import { Switch, SwitchDescription, SwitchGroup, SwitchLabel } from "@headlessui/vue"
import { Plus } from "lucide-vue-next"
import { utils } from "@/store/helpers"
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/vue"
import MetricFieldInput from "@/components/shared/MetricFieldInput.vue"
import Avatar from "@/components/shared/Avatar.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  name: "GlobalVariables",
  components: {
    MetricFieldInput,
    Avatar,
    Tab,
    TabGroup,
    TabList,
    TabPanel,
    TabPanels,
    Form,
    Field,
    TextArea,
    TextInput,
    Modal,
    SelectInput,
    Plus,
    Switch,
    SwitchDescription,
    SwitchGroup,
    SwitchLabel
  },
  inject: ["isModuleActivated", "isModuleVisible"],
  data() {
    return {
      schema: {
        name: Yup.string().required("Unique variable name is required"),
        description: Yup.string().nullable(),
        default_value: Yup.string().required("Default value is required"),
        is_per_user: Yup.mixed().oneOf(["t", "f"])
      },
      selected_tab_index: 0,
      options: [],
      option: "",
      to_be_deleted_object: null,
      show_object_delete_confirm_modal: false,
      initial_values: {},
      global_variables: [],
      global_variables_clone: [],
      input_types: [
        { label: this.$t("Checkbox"), value: "Checkbox" },
        { label: this.$t("Number"), value: "Number" },
        { label: this.$t("Decimal"), value: "Decimal" },
        { label: this.$t("Percent"), value: "Percent" },
        { label: this.$t("Currency"), value: "Currency" },
        { label: this.$t("Text"), value: "Text" },
        { label: this.$t("Textarea"), value: "Textarea" },
        { label: this.$t("Rich Textarea"), value: "RichTextarea" },
        // { label: "URL", value: "URL" },
        // { label: "Time", value: "Time" },
        // { label: "Date", value: "Date" },
        // { label: "Date Time", value: "DateTime" },
        // { label: "Select", value: "Select" },
        // { label: "Checklist", value: "Checklist" },
        { label: this.$t("Bar rating"), value: "BarRating" },
        { label: this.$t("Star rating"), value: "StarRating" }
      ]
    }
  },
  computed: {},

  // activated() {
  //   this.initVars()
  // },
  created() {
    this.initVars()
    this.initial_values = {
      name: "",
      is_public: "t",
      default_value: null,
      input_type: "Number",
      input_options: { theme: "snow" },
      decimal_places: 2,
      values: [],
      currency: "USD"
    }
  },
  methods: {
    addOption() {
      if (this.option != null && this.option != " ") {
        this.options.push(this.option)
      }
      this.option = null
    },
    initVars() {
      this.$store.dispatch("settings/getModulesSettings").then(() => {
        let global_variables =
          utils.find(this.$store.state.settings.modules_settings, { reference_code: "global_variables" })?.global_variables || []
        this.global_variables = []
        global_variables.forEach((variable, index) => {
          this.global_variables.push({
            index: index,
            organization: variable.organization,
            name: variable.name,
            reference_name: variable.reference_name,
            description: variable.description,
            default_value: variable.default_value,
            input_type: variable.input_type,
            input_options: variable.input_options || { theme: "snow" },
            id: variable.id,
            values: variable.values,
            schema: Object.fromEntries(variable.values.map((x) => [`${variable.reference_name}_${x.user}`, Yup.string().required()])),
            initial_values: Object.fromEntries(variable.values.map((x) => [`${variable.reference_name}_${x.user}`, x.value])),
            is_per_user: variable.is_per_user ? "t" : "f"
          })
        })
        this.global_variables_clone = this.$utils.clone(this.global_variables)
      })
    },
    removeGlobalVariable(obj) {
      this.to_be_deleted_object = null
      if (obj?.id) {
        this.to_be_deleted_object = obj
        this.show_object_delete_confirm_modal = true
      } else {
        this.global_variables = this.global_variables.filter((x) => x.index != obj.index)
      }
    },
    confirmObjectDelete() {
      this.global_variables = this.global_variables.filter((x) => x.id != this.to_be_deleted_object.id)
      this.$store.dispatch("settings/setSetting", {
        reference_code: "global_variables",
        global_variables: this.global_variables.map((x) => {
          return {
            name: x.name,
            object_type: x.object_type,
            renewal_base_metric: x.renewal_base_metric.id,
            renewal_date_metric: x.renewal_date_metric.id
          }
        })
      })
      this.$store.commit("settings/setSetting", {
        reference_code: "global_variables",
        global_variables: this.global_variables
      })
      this.show_object_delete_confirm_modal = false
      this.$emitter.emit(ALL_EMITTERS.DELETE_FORECAST, this.to_be_deleted_object.id)
      this.$toast.open({
        message: this.$t("Global variable") + ` ${this.to_be_deleted_object.name} ` + this.$t("has been removed"),
        position: "top",
        type: "success",
        duration: 3000
      })
    },
    addVariable() {
      this.global_variables.push({
        name: "",
        default_value: null,
        values: [],
        input_type: "Number",
        input_options: { theme: "snow" },
        index: this.global_variables.length + 1,
        is_per_user: "t",
        organization: this.$store.state.organization.id
      })
    },
    isGlobalVariableChanged(global_variable, option = null) {
      if (option) {
        let initial_global_variable = this.global_variables_clone.find((item) => item.index == global_variable.index)
        return !initial_global_variable || !this.$utils.isEqual(global_variable[option], initial_global_variable[option])
      } else {
        let initial_global_variable = this.global_variables_clone.find((item) => item.index == global_variable.index)
        return !initial_global_variable || !this.$utils.isEqual(global_variable, initial_global_variable)
      }
    },
    isGlobalVariableValid(global_variable) {
      return utils.isNotEmpty(global_variable.input_type) && utils.isNotEmpty(global_variable.name)
    },
    updateFieldValue(global_variable, user, value) {
      let field = global_variable.values.find((item) => item.user == user)
      if (field) {
        field.value = value
      } else {
        global_variable.values.push({ user: user, value: value })
      }
    },

    save(global_variable) {
      global_variable["target"] = true
      global_variable["values"] = utils.getItem(this.global_variables, global_variable, "index").values
      this.$utils.replaceItem(this.global_variables, global_variable, "index")

      this.$store
        .dispatch("settings/setSetting", {
          reference_code: "global_variables",
          global_variables: this.global_variables
        })
        .then(() => {
          this.initVars()
        })

      this.global_variables_clone = this.$utils.clone(this.global_variables)

      this.$toast.open({
        message: this.$t("Global variable created!"),
        position: "top",
        type: "success",
        duration: 3000
      })
    }
  }
}
</script>

<style>
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
